import {
  ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';

import { EventServicesService } from 'src/app/services/event-services.service';
import { GlobalServicesService } from 'src/app/services/global-services.service';

Chart.register(...registerables);

interface ChartPoint {
  label: string;
  value: number;
  color: string;
}

interface OverviewKpis {
  total_registrations: number;
  active_in_pipeline: number;
  top_source: string | null;
  top_source_count: number;
}

interface StagePipelineItem {
  stage_uuid: string;
  name: string;
  type: string;
  order: number | null;
  count: number;
}

interface EventMeta {
  name: string;
  type: string | null;
  category: string | null;
  event_start_date: string | null;
  event_end_date: string | null;
  registration_end_date: string | null;
  total_stages: number;
}

interface ActionItem {
  key: string;
  label: string;
  tag: string;
  severity: string;
  count: number;
}

interface OverviewResponse {
  event: EventMeta;
  kpis: OverviewKpis;
  stage_pipeline: StagePipelineItem[];
  action_needed: ActionItem[];
}

interface TimelineView {
  startDate: string;
  endDate: string;
  elapsedPct: number;
  daysRemaining: number;
  regEndDate: string | null;
  regCloseDays: number | null;
  eventEnded: boolean;
  regClosed: boolean;
}

interface OverviewTile {
  key: string;
  label: string;
  count: number;
}

interface StageBreakdown {
  stage_uuid: string;
  name: string;
  type: string;
  total: number;
  entered: number;
  moved_on: number;
  tiles: OverviewTile[];
}

/**
 * Event Overview tab — real-time, event-specific analytics.
 *
 * Loads the summary (KPIs + stage pipeline) in one call on init, then lazily
 * loads a stage's status breakdown when the user clicks a stage. No data is
 * computed here; everything comes from the backend overview endpoints.
 */
@Component({
  selector: 'app-event-overview',
  templateUrl: './event-overview.component.html',
  styleUrls: ['./event-overview.component.css'],
  standalone: false,
})
export class EventOverviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() eventId = '';

  overview: OverviewResponse | null = null;
  loadingOverview = false;
  overviewError = false;
  userPrivileges: Set<string> = new Set();

  selectedStage: StagePipelineItem | null = null;
  stageBreakdown: StageBreakdown | null = null;
  stageChartData: ChartPoint[] = [];
  pipelineChartData: ChartPoint[] = [];
  stageLegend: { label: string; color: string }[] = [];
  pipelineLegend: { label: string; color: string }[] = [];
  breakdownView: 'table' | 'chart' = 'table';
  loadingStage = false;

  showDownloadMenu = false;
  isGeneratingPdf = false;

  buildingReport = false;
  reportBreakdowns: {
    stage: StagePipelineItem; tiles: OverviewTile[]; total: number; entered: number; moved_on: number;
  }[] = [];

  timeline: TimelineView | null = null;
  today = new Date();

  private destroy$ = new Subject<void>();
  private breakdownChart?: Chart;
  private breakdownCanvas?: ElementRef<HTMLCanvasElement>;

  private pipelineChart?: Chart;
  private pipelineCanvas?: ElementRef<HTMLCanvasElement>;

  /** Fires whenever the breakdown (selected-stage) canvas is added/removed. */
  @ViewChild('breakdownCanvas') set canvasRef(el: ElementRef<HTMLCanvasElement> | undefined) {
    this.breakdownCanvas = el;
    this.renderBreakdownChart();
  }

  /** Fires whenever the overview pipeline canvas (no-stage-selected) is added/removed. */
  @ViewChild('pipelineCanvas') set pipelineCanvasRef(el: ElementRef<HTMLCanvasElement> | undefined) {
    this.pipelineCanvas = el;
    this.renderPipelineChart();
  }

  @ViewChild('reportArea') reportArea?: ElementRef<HTMLElement>;

  /** Stage-type dot colour — matches the workflow stage-type tag palette. */
  private readonly stageColors: Record<string, string> = {
    'Registration': '#9333ea',
    'Assessment': '#ef4444',
    'Online Interview': '#15b8a5',
    'Offline Interview': '#805AD5',
    'AI Interview': '#4F46E5',
    'Documentation': '#e8b00b',
    'Offer': '#22c55d',
    'Onboarding': '#0fa2e8',
  };

  /** Stage-type tag class — same mapping the workflow component uses. */
  private readonly stageTagClasses: Record<string, string> = {
    'Registration': 'tag-eligibility',
    'Assessment': 'tag-test',
    'Online Interview': 'tag-interview',
    'Offline Interview': 'tag-offline-interview',
    'AI Interview': 'tag-ai-interview',
    'Documentation': 'tag-screening',
    'Offer': 'tag-offer',
    'Onboarding': 'tag-onboarding',
  };

  /** Chart slice palette — same cohesive set the main dashboard charts use. */
  private readonly chartPalette = [
    '#8B5CF6', '#6366F1', '#EC4899', '#10B981', '#F59E0B', '#14B8A6', '#A78BFA',
  ];

  /**
   * Interview-type stages keep their Interview-tab redirect for the session statuses;
   * tile key (status slug) -> Interview tab id. Other statuses fall back to the Stages table.
   */
  private readonly INTERVIEW_TYPES = ['Online Interview', 'Offline Interview', 'AI Interview'];
  private readonly interviewTabByStatus: Record<string, string> = {
    lobby: 'lobby',
    ongoing: 'ongoing',
    completed: 'completed',
    cancelled: 'cancelled',
  };

  constructor(
    private eventService: EventServicesService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private globalService: GlobalServicesService,
  ) {}

  ngOnInit(): void {
    this.globalService.privilegeDetailsSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((privileges) => {
        this.userPrivileges = privileges;
      });
  }

  tileHasView(tile: OverviewTile): boolean {
    const stage = this.selectedStage;
    if (!stage || !this.userPrivileges.has('event_stage.view')) return false;
    if (this.INTERVIEW_TYPES.includes(stage.type)) {
      return !!this.interviewTabByStatus[tile.key];
    }
    return true;
  }


  openTileView(tile: OverviewTile): void {
    const stage = this.selectedStage;
    if (!stage) return;

    const interviewTab = this.INTERVIEW_TYPES.includes(stage.type)
      ? this.interviewTabByStatus[tile.key]
      : undefined;
    if (interviewTab) {
      this.router.navigate(['events/', this.eventId], {
        queryParams: { view: 'interview', subStatus: interviewTab, stage: stage.stage_uuid },
      });
      return;
    }

    this.router.navigate(['events/', this.eventId], {
      queryParams: { view: 'workflow', stageId: stage.stage_uuid, status: tile.label },
    });
  }

  toggleDownloadMenu(): void {
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  @HostListener('document:click')
  closeDownloadMenu(): void {
    if (this.showDownloadMenu) {
      this.showDownloadMenu = false;
    }
  }

  private async fetchAllBreakdowns(): Promise<
    { stage: StagePipelineItem; tiles: OverviewTile[]; total: number; entered: number; moved_on: number }[]
  > {
    const stages = this.overview?.stage_pipeline ?? [];
    return Promise.all(
      stages.map((stage) =>
        firstValueFrom(this.eventService.getEventOverviewStageFastApi(this.eventId, stage.stage_uuid))
          .then((res) => {
            const b = res.body as StageBreakdown;
            return { stage, tiles: b.tiles, total: b.total, entered: b.entered, moved_on: b.moved_on };
          })
          .catch(() => ({
            stage, tiles: [] as OverviewTile[], total: 0, entered: 0, moved_on: 0,
          })),
      ),
    );
  }

  async downloadPdf(): Promise<void> {
    this.showDownloadMenu = false;
    if (this.isGeneratingPdf || !this.overview) {
      return;
    }
    this.isGeneratingPdf = true;
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      this.reportBreakdowns = await this.fetchAllBreakdowns();
      this.buildingReport = true;
      this.cdr.detectChanges();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const target = this.reportArea?.nativeElement;
      if (target) {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const contentWidth = pageWidth - margin * 2;
        let cursorY = margin;
        const blocks = Array.from(target.children) as HTMLElement[];
        for (const block of blocks) {
          const canvas = await html2canvas(block, {
            scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff',
          });
          const imgHeight = (canvas.height * contentWidth) / canvas.width;
          if (cursorY + imgHeight > pageHeight - margin && cursorY > margin) {
            pdf.addPage();
            cursorY = margin;
          }
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.8), 'JPEG', margin, cursorY, contentWidth, imgHeight);
          cursorY += imgHeight + 4;
        }
        pdf.save(`${this.exportFileBase()}.pdf`);
        this.messageService.add({
          severity: 'success', summary: 'Downloaded', detail: 'File downloaded successfully',
        });
      }
    } catch (error) {
      console.error('Failed to generate overview PDF', error);
      this.messageService.add({
        severity: 'error', summary: 'Download failed',
        detail: 'Could not generate the PDF. Please try again.',
      });
    } finally {
      this.buildingReport = false;
      this.isGeneratingPdf = false;
    }
  }

  async downloadCsv(): Promise<void> {
    this.showDownloadMenu = false;
    if (!this.overview) {
      return;
    }
    const { event, kpis, stage_pipeline, action_needed } = this.overview;
    const rows: string[][] = [
      ['Event', event.name],
      ['Type', event.type ?? ''],
      ['Category', event.category ?? ''],
      [],
      ['KPI', 'Value'],
      ['Total registered', String(kpis.total_registrations)],
      ['Active in pipeline', String(kpis.active_in_pipeline)],
      ['Top source', kpis.top_source ? `${kpis.top_source} (${kpis.top_source_count})` : '-'],
      [],
      ['Stage', 'Type', 'Candidates'],
      ...stage_pipeline.map((s) => [s.name, s.type, String(s.count)]),
    ];

    const breakdowns = await this.fetchAllBreakdowns();
    breakdowns.forEach(({ stage, tiles, entered, moved_on }) => {
      rows.push(
        [],
        [`${stage.name} (${stage.type}) — ${entered} entered · ${moved_on} moved on`],
        ['Status', 'Count'],
      );
      if (tiles.length) {
        tiles.forEach((t) => rows.push([t.label, String(t.count)]));
      } else {
        rows.push(['No breakdown for this stage type', '']);
      }
    });

    if (action_needed.length) {
      rows.push([], ['Action needed', 'Tag', 'Count']);
      action_needed.forEach((a) => rows.push([a.label, a.tag, String(a.count)]));
    }

    const csv = rows.map((r) => r.map((c) => this.csvCell(c)).join(',')).join('\r\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.exportFileBase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    this.messageService.add({
      severity: 'success', summary: 'Downloaded', detail: 'File downloaded successfully',
    });
  }

  private csvCell(value: string): string {
    let v = value ?? '';
    if (/^[=+\-@\t\r]/.test(v)) {
      v = `'${v}`;
    }
    return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  }

  private exportFileBase(): string {
    const name = this.overview?.event?.name || 'event-overview';
    return `${name.replace(/[^\w.-]+/g, '_')}_overview`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventId'] && this.eventId) {
      this.resetState();
      this.loadOverview();
    }
  }

  ngOnDestroy(): void {
    this.breakdownChart?.destroy();
    this.pipelineChart?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOverview(): void {
    this.today = new Date();
    this.loadingOverview = true;
    this.overviewError = false;
    this.eventService.getEventOverviewFastApi(this.eventId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.overview = res.body as OverviewResponse;
          this.buildTimeline(this.overview?.event);
          this.pipelineChartData = this.buildPipelineChartData(this.overview?.stage_pipeline ?? []);
          this.pipelineLegend = this.buildLegend(this.pipelineChartData);
          this.loadingOverview = false;
        },
        error: () => {
          this.overviewError = true;
          this.loadingOverview = false;
        },
      });
  }

  selectStage(stage: StagePipelineItem): void {
    if (this.selectedStage?.stage_uuid === stage.stage_uuid) {
      return;
    }
    this.selectedStage = stage;
    this.stageBreakdown = null;
    this.stageChartData = [];
    this.stageLegend = [];
    this.breakdownView = 'table';
    this.loadingStage = true;
    this.eventService.getEventOverviewStageFastApi(this.eventId, stage.stage_uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (this.selectedStage?.stage_uuid !== stage.stage_uuid) {
            return;
          }
          this.stageBreakdown = res.body as StageBreakdown;
          this.stageChartData = this.buildChartData(this.stageBreakdown.tiles);
          this.stageLegend = this.buildLegend(this.stageChartData);
          this.loadingStage = false;
        },
        error: () => {
          this.loadingStage = false;
        },
      });
  }

  retry(): void {
    this.loadOverview();
  }

  stageColor(type: string): string {
    return this.stageColors[type] ?? '#718096';
  }

  stageTagClass(type: string): string {
    return this.stageTagClasses[type] ?? 'tag-default';
  }

  trackByStage(_: number, stage: StagePipelineItem): string {
    return stage.stage_uuid;
  }

  trackByTile(_: number, tile: OverviewTile): string {
    return tile.key;
  }

  private resetState(): void {
    this.overview = null;
    this.selectedStage = null;
    this.stageBreakdown = null;
    this.stageChartData = [];
    this.pipelineChartData = [];
    this.stageLegend = [];
    this.pipelineLegend = [];
    this.timeline = null;
  }

  tileColor(index: number): string {
    return this.chartPalette[index % this.chartPalette.length];
  }

  private buildPipelineChartData(stages: StagePipelineItem[]): ChartPoint[] {
    return stages
      .map((s, i) => ({ label: s.name, value: s.count, color: this.tileColor(i) }))
      .filter((p) => p.value > 0);
  }

  private buildChartData(tiles: OverviewTile[]): ChartPoint[] {
    return tiles
      .map((t, i) => ({ label: t.label, value: t.count, color: this.tileColor(i) }))
      .filter((p) => p.value > 0);
  }

  private buildLegend(points: ChartPoint[]): { label: string; color: string }[] {
    return points.map((p) => ({ label: p.label, color: p.color }));
  }

  private doughnutConfig(points: ChartPoint[]): any {
    return {
      type: 'doughnut',
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            data: points.map((p) => p.value),
            backgroundColor: points.map((p) => p.color),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          // Legend is rendered as HTML below the canvas so it can pin to the
          // bottom of the card while the ring stays a fixed compact size.
          legend: { display: false },
          tooltip: {
            backgroundColor: '#fff',
            titleColor: '#111827',
            bodyColor: '#6b7280',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            padding: 14,
            boxPadding: 8,
            usePointStyle: true,
            displayColors: false,
            titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' },
            bodyFont: { size: 12, family: 'Poppins, sans-serif' },
          },
        },
      },
    };
  }

  /** Render (or clear) the breakdown doughnut for the current stage's data. */
  private renderBreakdownChart(): void {
    this.breakdownChart?.destroy();
    this.breakdownChart = undefined;

    const canvas = this.breakdownCanvas?.nativeElement;
    if (!canvas || !this.stageChartData.length) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.breakdownChart = new Chart(ctx, this.doughnutConfig(this.stageChartData));
  }

  /** Derive timeline progress (% elapsed, days remaining) from the event dates. */
  private buildTimeline(meta?: EventMeta | null): void {
    if (!meta?.event_start_date || !meta?.event_end_date) {
      this.timeline = null;
      return;
    }
    const dayMs = 86_400_000;
    const start = new Date(meta.event_start_date).getTime();
    const end = new Date(meta.event_end_date).getTime();
    const now = Date.now();
    const span = Math.max(end - start, 1);

    let regCloseDays: number | null = null;
    let regClosed = false;
    if (meta.registration_end_date) {
      const regEnd = new Date(meta.registration_end_date).getTime();
      regClosed = regEnd < now;
      regCloseDays = Math.max(0, Math.ceil((regEnd - now) / dayMs));
    }

    this.timeline = {
      startDate: meta.event_start_date,
      endDate: meta.event_end_date,
      elapsedPct: Math.round(Math.min(100, Math.max(0, ((now - start) / span) * 100))),
      daysRemaining: Math.max(0, Math.ceil((end - now) / dayMs)),
      regEndDate: meta.registration_end_date,
      regCloseDays,
      eventEnded: end < now,
      regClosed,
    };
  }

  /** Render the overview pipeline bar (stage names + counts) for the empty panel. */
  private renderPipelineChart(): void {
    this.pipelineChart?.destroy();
    this.pipelineChart = undefined;

    const canvas = this.pipelineCanvas?.nativeElement;
    if (!canvas || !this.pipelineChartData.length) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.pipelineChart = new Chart(ctx, this.doughnutConfig(this.pipelineChartData));
  }
}
